import { useState, useRef, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { API, Auth, Hub } from "aws-amplify";

export default function useAccountAdminContracts(accountId) {
  const history = useHistory();
  const [addingContract, setAddingContract] = useState(false);
  const [ready, setReady] = useState(false); // only true when an auth session exists
  const inFlight = useRef(false);

  // Track auth state so we don't fire while logged out
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (mounted) setReady(true);
      } catch {
        if (mounted) setReady(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (mounted) setReady(true);
        } catch {
          if (mounted) setReady(false);
        }
      } else if (evt === "signOut") {
        if (mounted) {
          setReady(false);
          setAddingContract(false);
          inFlight.current = false;
        }
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  const addContract = useCallback(async () => {
    if (!accountId) {
      console.warn("addContract called without accountId");
      return;
    }
    if (inFlight.current) return;

    // Ensure we’re authenticated right now
    if (!ready) {
      try {
        await Auth.currentAuthenticatedUser();
        setReady(true);
      } catch {
        console.warn("addContract blocked: no authenticated session");
        return;
      }
    }

    inFlight.current = true;
    setAddingContract(true);

    try {
      const nowIso = new Date().toISOString();
      const request = {
        body: {
          dates: { start: nowIso, end: nowIso },
          scopes: ["prn::::"],
          agreement: ""
        },
        withCredentials: true
      };

      const contractData = await API.post(
        "AccountGatewayApi",
        `/account/${accountId}/contract`,
        request
      );

      history.push(`/ContractAdmin/${contractData.id}`);
    } catch (err) {
      console.error("error creating contract", err);
    } finally {
      setAddingContract(false);
      inFlight.current = false;
    }
  }, [accountId, history, ready]);

  return { addContract, addingContract, ready };
}
